import { Link } from '@tanstack/react-router';
import { Download, FolderOpen, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollReveal, ScrollRevealStagger } from '@/components/common/ScrollReveal';
import { AppMockupFrame, MockPhotoGrid, MockMetadataPanel } from './mockups';

/**
 * DesktopAppSection
 * デスクトップアプリのメインショーケースセクション（ダーク背景）
 * アプリモックアップ + 3機能カード + ダウンロードCTA
 */
export function DesktopAppSection() {
  return (
    <section className="bg-[#0a0a0a] px-6 md:px-16 lg:px-24 py-24 md:py-32 text-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid md:grid-cols-2 gap-12 items-end mb-12">
          <ScrollReveal direction="up">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              デスクトップアプリで
              <br />
              すべてが完結
            </h2>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <p className="text-base text-white/60 leading-relaxed">
              VRChatの写真フォルダを指定するだけ。写真に埋め込まれたワールド名や撮影者情報を自動抽出し、VRChatログから一緒にいたフレンドも特定。整理した写真はクラウドに即座に同期されます。
            </p>
          </ScrollReveal>
        </div>

        {/* App Mockup */}
        <ScrollReveal direction="up" delay={0.3}>
          <AppMockupFrame variant="dark" title="Omokage Desktop" className="mb-16">
            {/* Tab Bar */}
            <div className="flex border-b border-white/[0.06] bg-white/[0.02]">
              <div className="relative px-6 py-3 text-[13px] text-accent cursor-default">
                ローカル
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent" />
              </div>
              <div className="px-6 py-3 text-[13px] text-white/35 cursor-default">クラウド</div>
            </div>

            {/* Body: Grid + Sidebar */}
            <div className="grid lg:grid-cols-[1fr_300px]">
              <MockPhotoGrid
                columns={6}
                cellCount={18}
                variant="dark"
                selectedIndex={2}
                showDates
              />
              <MockMetadataPanel className="max-lg:border-l-0 max-lg:border-t max-lg:border-white/[0.06]" />
            </div>
          </AppMockupFrame>
        </ScrollReveal>

        {/* Feature Cards */}
        <ScrollRevealStagger staggerDelay={0.15}>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="p-8 bg-white/[0.03] border border-white/[0.06] rounded-lg">
              <FolderOpen className="w-6 h-6 text-accent mb-4" strokeWidth={2} />
              <h4 className="text-base font-semibold text-white/90 mb-2">
                ローカルフォルダスキャン
              </h4>
              <p className="text-[13px] text-white/50 leading-relaxed">
                VRChatの写真フォルダを指定するだけ。何千枚の写真も即座に一覧表示されます。
              </p>
            </div>

            <div className="p-8 bg-white/[0.03] border border-white/[0.06] rounded-lg">
              <Sparkles className="w-6 h-6 text-accent mb-4" strokeWidth={2} />
              <h4 className="text-base font-semibold text-white/90 mb-2">メタデータ自動抽出</h4>
              <p className="text-[13px] text-white/50 leading-relaxed">
                写真に埋め込まれたワールド情報や撮影者情報を自動で読み取り。手動入力は不要です。
              </p>
            </div>

            <div className="p-8 bg-white/[0.03] border border-white/[0.06] rounded-lg">
              <Users className="w-6 h-6 text-accent mb-4" strokeWidth={2} />
              <h4 className="text-base font-semibold text-white/90 mb-2">VRChatログ解析</h4>
              <p className="text-[13px] text-white/50 leading-relaxed">
                VRChatのログファイルを解析し、一緒にいたフレンドやインスタンス情報を写真に自動付与します。
              </p>
            </div>
          </div>
        </ScrollRevealStagger>

        {/* Download CTA */}
        <ScrollReveal direction="up" delay={0.2}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <Button asChild size="lg" className="bg-accent text-white hover:bg-accent/90 gap-2">
              <Link to="/download">
                <Download className="w-[18px] h-[18px]" />
                Windows版をダウンロード
              </Link>
            </Button>
            <span className="text-[13px] text-white/35">Windows 10/11対応 &middot; 無料</span>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
